import React, { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper, Typography } from "@mui/material";
import { ArrowBack, Edit, Download } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { dayCount, formatDate } from "packages/novous_web/src/utils/helper";
import DownloadData from "packages/novous_web/src/common/components/DownloadData";
import useAxios from "packages/novous_web/src/api/useAxios";
import { API_ENDPOINTS } from "packages/novous_web/src/api/endpoints";
import { useAuth } from "packages/novous_web/src/authContext/useAuth";
import { useToast } from "packages/novous_web/src/context/ToastContext";
import CustomIconButton from "packages/novous_web/src/common/components/CustomIconButton";
import Delete from 'packages/novous_web/src/assets/icons/Delete.svg'
import edit from 'packages/novous_web/src/assets/icons/edit.svg'




// Status Styles
const statusStyles: Record<string, any> = {
  Active: { backgroundColor: "#D9EDE6", color: "#219653", padding: '4px 8px', textAlign: "center", fontWeight: "400" },
  Paused: { backgroundColor: "#FBF1D2", color: "#A9861E", padding: '4px 8px', textAlign: "center", fontWeight: "400" },
  Good: { fontWeight: "bold", },
  Strong: { fontWeight: "bold", },
  Detected: { fontWeight: "bold", },
  No: { fontWeight: "bold" },
};

const DeviceDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const deviceData = location.state; // Getting data from route
  const { user } = useAuth();
  const { showToast } = useToast();
  const [selectedFormat, setSelectedFormat] = useState<string>('pdf');
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // State for delete modal
  const [deleteingDevice, setDeletingDevice] = useState<{
    groupName?: string;
  } | null>(null);


  console.log(deviceData, '----------------deviceData-----------');


  const {
    data: downloadDeviceData,
    error: downloadDeviceError,
    loading: ownloadDeviceLoading,
    fetchData: fetchDownloadDeviceData,
  } = useAxios<any>(`${API_ENDPOINTS.directories.DEVICE_DOWNLOAD}`, "POST", 'blob');
  const {
    data: deviceDeleteData,
    error: deviceDeleteError,
    loading: deviceDeleteLoading,
    fetchData: deleteDevice,
  } = useAxios<any>(`${API_ENDPOINTS.directories.DEVICE_DELETE}`, "POST");

  if (!deviceData) {
    return <Typography variant="h6" color="error">No Data Available</Typography>;
  }

  const getFaultType = (deviceData: any): string => {
    if (!deviceData) return "N/A"; // Handle null or undefined cases

    const statusMap: { [key: string]: string } = {
      gpsStatus: "GPS",
      fovStatus: "FOV",
      sdCardStatus: "SD Card",
      micStatus: "Microphone",
      sosStatus: "SOS",
    };

    // Filter and collect all statuses that are 1
    const faultList = Object.keys(statusMap)
      .filter((key) => deviceData[key] === 1) // Find keys with value 1
      .map((key) => statusMap[key]); // Get the corresponding fault name

    return faultList.length > 0 ? faultList.join(", ") : "N/A"; // Join faults or return "N/A"
  };

  // **Dynamic Sections from Route Data**
  const sections = [
    {
      title: "Device Identification",
      data: [
        { label: "Device ID", value: deviceData.deviceId },
        { label: "Device Type", value: deviceData.deviceType },
        { label: "Vehicle Reg Number", value: deviceData.vehicleRegistrationNo,  underline: true  },
        { label: "Vehicle Attached", value: deviceData.hasDevice ? "Yes" : "No" },
        { label: "Installed Date", value: deviceData.createdAt ? formatDate(deviceData.createdAt) : "N/A" },
        { label: "Expiration Date", value: deviceData.createdAt ? formatDate(deviceData.createdAt) : "N/A" },
        { label: "Fault Type", value: getFaultType(deviceData) },
        { label: "Detection time", value: deviceData.lastErrorOccuredTime ? dayjs(deviceData.lastErrorOccuredTime).format("DD MMM YYYY, HH:mm:ss") : "N/A" },
      ],
    },
    {
      title: "Camera Details",
      data: [
        { label: "DSM Camera ID", value: deviceData.dsmCameraId || "N/A" },
        { label: "In Cabin Camera ID", value: deviceData.inCabinCameraId || "N/A" },
        { label: "Rear Camera ID", value: deviceData.rearCameraId || "N/A" },
        { label: "", value: "" },
        { label: "Cabin Camera Status", value: deviceData.inCabinCameraStatus ? "Active" : "Paused", status: true },
        { label: "DSM Camera Status", value: deviceData.dmsCameraStatus ? "Active" : "Paused", status: true },
        { label: "FCW Camera Status", value: deviceData.fcwCameraStatus ? "Active" : "Paused", status: true },
        { label: "Rear Camera Status", value: deviceData.rearCameraStatus ? "Active" : "Paused", status: true },
      ],
    },
    {
      title: "Location and Activity",
      data: [
        { label: "Last Latitude", value: deviceData.lastLatitude || "N/A" },
        { label: "Last Longitude", value: deviceData.lastLongitude || "N/A" },
        { label: "", value: "" },
        { label: "", value: "" },
        { label: "Last Update Time", value: deviceData.updatedTime ? formatDate(deviceData.updatedTime) : "N/A" },
        { label: "Last Update Aging (Days)", value: dayCount(deviceData.updatedTime) },
        { label: "Last Alarm Time", value: deviceData.lastAlarmDataTime ? formatDate(deviceData.lastAlarmDataTime) : "N/A" },
        { label: "Last Alarm Aging(Days)", value: dayCount(deviceData.lastAlarmDataTime) },
      ],
    },
    {
      title: "GPS and Connectivity",
      data: [
        { label: "GPS Status", value: deviceData.gpsStatus ? "Active" : "Paused", status: true },
        { label: "Signal Status", value: deviceData.signalStrengthStatus ? "Strong" : "Weak", status: true },
      ],
    },
    {
      title: "Device Health and Status",
      data: [
        { label: "FOV Status", value: deviceData.fovStatus ? "Good" : "No", status: true },
        { label: "Power Tampering Status", value: deviceData.powerTamperingStatus ? "Detected" : "No", status: true },
        { label: "Status", value: deviceData.onlineState ? "Active" : "Paused", status: true },
      ],
    },
    {
      title: "Peripherals and Sensors",
      data: [
        { label: "Mic Status", value: !deviceData.micStatus ? "Working" : "Not Working" },
        { label: "SD Card Status", value: !deviceData.sdCardStatus ? "Inserted" : "Not Inserted" },
        { label: "Speaker Status", value: !deviceData.speakerStatus ? "Operational" : "Not Operational" },
        { label: "", value: "" },
        // { label: "", value: "" },
        { label: "IMU Status", value: !deviceData.imuStatus ? "Calibrated" : "Not Calibrated" },
        { label: "SOS Button Status", value: !deviceData.sosStatus ? "Functional" : "Not Functional" },
      ],
    },
    {
      title: "Environmental Factors",
      data: [
        { label: "Low Visibility Status", value: deviceData.lowVisibilityStatus ? "Detected" : "Not Detected" },
      ],
    },
  ];




  useEffect(() => {
    if (downloadDeviceData) {
      try {
        if (downloadDeviceData instanceof Blob) {
          const url = window.URL.createObjectURL(downloadDeviceData);
          const a = document.createElement('a');
          a.href = url;
          let extension = selectedFormat.toLowerCase();
          if (extension === 'excel') {
            extension = 'xlsx'; // Use xlsx for Excel format
          }
          const fileName = `device_data.${extension}`;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          console.error('Invalid response format. Expected a Blob.');
        }
      } catch (error) {
        console.error('Error downloading file:', error);
      } finally {
        setIsDownloadModalOpen(false);
      }
    }
  }, [downloadDeviceData])
  const handleDownloadModalClose = () => {
    setIsDownloadModalOpen(false);
  };
  const handleFormatChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFormat(event.target.value);
  };
  const handleDownload = async () => {
    fetchDownloadDeviceData({ vehicleId: deviceData?.vehicleRegistrationNo, fileType: selectedFormat });
  };


  const handleEditClick = (device: any) => {
    console.log(`directories/devices/edit/${device?.deviceId}`,device);
    
    navigate(`./../edit/${device?.deviceId}`)
  };

  // Delete handler
  const DeleteDevice = () => {
    deleteDevice(deleteingDevice)
  }
  const handleDeleteClick = (device: any) => {
    setIsDeleteModalOpen(true); // Open delete confirmation modal
    setDeletingDevice(device)
  };
  const handleDeleteConfirm = () => {
    DeleteDevice(); // Call delete endpoint
    setIsDeleteModalOpen(false); // Close modal
    setDeletingDevice(null)
  };
  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false); // Close modal without deleting
    setDeletingDevice(null)
  };
  useEffect(() => {
    if (deviceDeleteData?.message) {
      showToast(deviceDeleteData?.message, deviceDeleteData?.data);
      if (deviceDeleteData?.data === "success") {
        navigate(-1)
      }
    }

  }, [deviceDeleteData])



  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      {/* Back Button */}
      <div className="mb-4">
        <button className="text-blue-600 font-medium flex items-center" onClick={() => navigate(-1)}>
          <ArrowBack className="mr-1" /> Back
        </button>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <div>
          <Typography variant="h5" fontWeight={600}>
            {deviceData?.groupName}
            {/* FleetTracker X100 */}
          </Typography>
          <Typography variant="body2" fontWeight={400} color="secondary">
            Created on:{" "}
            <Typography component="span" variant="body2" fontWeight={400} color="text">
              {deviceData?.createdAt ? formatDate(deviceData?.createdAt) : "Unknown"}
            </Typography>
          </Typography>
        </div>
        <div className="flex gap-3 mt-4 md:mt-0">
        {/* <CustomIconButton
            icon={<img src={edit} alt="Custom" width={16} height={16} />}
            text="Edit"
            background='transparent'
            outline={false} // Set to true if you want an outline
            onClick={() => handleEditClick(deviceData)}
          /> */}
          {/* <CustomIconButton
            icon={<img src={Delete} alt="Custom" width={16} height={16} />}
            text="Delete"
            color="error"
            background='transparent'
            outline={false} // Set to true if you want an outline
            onClick={() => handleDeleteClick(deviceData)}
          /> */}
          <Button variant="contained" color="primary" startIcon={<Download />}
            onClick={() => setIsDownloadModalOpen(true)}
          >
            Download
          </Button>
        </div>
      </div>

      {/* Sections */}
      {
        sections.map((section, index) => (
          <Paper key={index} className="p-6 mb-4 " sx={{ borderRadius: "12px" }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {section.title}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {section.data.map((item: {
                label: string;
                value: string;
                status?: boolean;
                underline?: boolean;
              }, idx) => (
                <Box
                  key={idx}
                  sx={{
                    flex: "1 1 calc(25% - 16px)",
                    maxWidth: "calc(25% - 16px)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1
                  }}
                >
                  <Typography variant="body2" fontWeight={400} color="secondary">
                    {item.label}
                  </Typography>
                  {!item.status ?
                    <Typography
                      variant="button"
                    sx={item?.underline ? { textDecoration: 'underline' } : {}}
                    >
                      {item.value}
                    </Typography>
                    :

                    <Typography variant="body2" fontWeight={600}
                      style={{
                        textAlign: "left",
                        borderRadius: '8px',
                        minWidth: '60px',
                        lineHeight: "18px",
                        width: "max-content",
                        ...statusStyles[item.value]
                      }}>
                      {item.value}
                    </Typography>
                  }
                </Box>
              ))}
            </Box>
          </Paper>
        ))
      }

      <DownloadData
        isDownloadModalOpen={isDownloadModalOpen}
        handleDownloadModalClose={handleDownloadModalClose}
        selectedFormat={selectedFormat}
        handleFormatChange={handleFormatChange}
        handleDownload={handleDownload}
        modalTitle="Download Device Data"
        modalDescription="Please select a file format for your download."
        formatLabel="Format"
        formatExcelLabel="Excel"
        formatCsvLabel="CSV"
        formatPdfLabel="PDF"
        cancelButtonText="Cancel"
        downloadButtonText="Download"
      />

      {/* Delete Confirmation Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Device</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to permanently delete {deleteingDevice?.groupName}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div >
  );
};

export default DeviceDetails;
